import type { ReactNode } from 'react';

import classNames from 'classnames';

import styles from './Footer.module.css';

import { Divider } from '../Divider';
import { Grid, GridItem } from '../Grid';
import { Link } from '../Link';
import { Nowrap } from '../Nowrap';
import { Section } from '../Section';
import { Text } from '../Text';

export type FooterItem = ReactNode;
export type FooterSection = [
  /** The section title, rendered inside an heading level 4. */
  label: ReactNode,
  /** The items (e.g. links) to list in the section */
  items: FooterItem[],
];

export interface FooterProps {
  /** The sections of links to include in the footer. Max 3 sections. */
  sections: FooterSection[];
  /** Relative or absolute link to the privacy page. */
  privacyLink: string;
  /** Relative or absolute link to the terms page. */
  termsLink: string;
  /** Relative or absolute link to the accessibility statement page. */
  accessibilityLink: string;
  /** Relative or absolute link to the do-not-sell-my-information page. */
  doNotSellLink: string;
  /** Toggle off the tagline ("Our food is made..."). */
  hideTagline?: boolean;
  className?: string;
}

/**
 * Create a footer with up to 3 sections of links.
 *
 * Each section has a heading and a list of React elements (usually links). Hide
 * the tag-line ("Our food is made...") with the `hideTagline` prop.
 *
 * @example
 *
 * ```
 * const sectionA = [
 *   <Link to="/page1">Link 1</Link>,
 *   <Link to="/page3">Link 3</Link>,
 * ];
 * const sectionB = [
 *   <Link to="/pageA">Link A</Link>,
 *   <Link to="/pageB">Link B</Link>,
 *   <Link to="/pageC">Link C</Link>,
 * ];
 * const sectionC = [
 *   <Link to="/pageD">Link D</Link>,
 *   <Link to="/pageE">Link E</Link>,
 *   <Link to="/pageF">Link F</Link>,
 * ];
 * <Footer
 *   sections={[
 *     ['Section A Heading', sectionA],
 *     ['Section B Heading', sectionB],
 *     ['Section C Heading', sectionC],
 *   ]}
 *   doNotSellLink="/do-not-sell"
 *   privacyLink="/privacy"
 *   termsLink="/terms"
 *   accessibilityLink="/accessibility"
 * />;
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/footer
 */
export function Footer(props: FooterProps) {
  return (
    <Section as="footer" className={classNames(styles.root, props.className)}>
      <Grid
        className={classNames(styles.container, {
          [styles.hideTagline]: props.hideTagline,
        })}
        hSpacing="md"
      >
        <GridItem>
          <Grid className={styles.sections}>
            {props.sections.slice(0, 3).map(([label, items], i) => (
              <GridItem key={i} md={4} className={styles.section}>
                <Grid vSpacing="none">
                  {label && (
                    <GridItem>
                      <Text
                        color="White"
                        as="h4"
                        topSpacing="none"
                        bottomSpacing="sm"
                      >
                        {label}
                      </Text>
                    </GridItem>
                  )}
                  <GridItem>
                    <ul className={styles.itemsList}>
                      {items.map((item, j) => (
                        <li key={j}>
                          <Text color="White">{item}</Text>
                        </li>
                      ))}
                    </ul>
                  </GridItem>
                </Grid>
              </GridItem>
            ))}
          </Grid>
          {!props.hideTagline && (
            <GridItem className={styles.tagline}>
              <Text color="White" as="p" topSpacing="none" align="center">
                Our food is prepared to the sweet sounds of doo-wop and shipped{' '}
                <Nowrap>with ❤️</Nowrap> in the <Nowrap>continental US</Nowrap>
              </Text>
            </GridItem>
          )}
          <Divider color="White" borderStyle="dotted" margin={0} width={4} />
          <Grid className={styles.legalSectionContainer} rowGap="sm">
            <GridItem className={styles.legalSection}>
              <ul className={styles.legalItemsList}>
                <li>
                  <Text color="White">
                    © {new Date().getFullYear()} The Farmer’s Dog Inc.
                  </Text>
                </li>
                <li>
                  <Text color="White">Long Live Dogs&trade;</Text>
                </li>
              </ul>
              <ul className={styles.legalItemsList}>
                <li>
                  <Text color="White">
                    <Link
                      className={styles.legalItemLink}
                      to={props.privacyLink}
                      baseColor="white"
                    >
                      Privacy
                    </Link>
                  </Text>
                </li>
                <li>
                  <Text color="White">
                    <Link
                      className={styles.legalItemLink}
                      to={props.termsLink}
                      baseColor="white"
                    >
                      Terms
                    </Link>
                  </Text>
                </li>
                <li>
                  <Text color="White">
                    <Link
                      className={styles.legalItemLink}
                      to={props.accessibilityLink}
                      baseColor="white"
                    >
                      Accessibility
                    </Link>
                  </Text>
                </li>
                <li>
                  <Text color="White">
                    <Link
                      className={styles.legalItemLink}
                      to={props.doNotSellLink}
                      baseColor="white"
                    >
                      Do Not Sell My Personal Information
                    </Link>
                  </Text>
                </li>
              </ul>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </Section>
  );
}
